'use client';

import FullCalendar from '@fullcalendar/react';
import { useCreateOrModifyAppointmentStore } from '~/stores/create-appointment-store';
import { useShallow } from 'zustand/react/shallow';
import { BookableItemSearch } from './BookableItemSearch';
import { StepList } from './StepList';
import { DateTimeSelector } from './DateTimeSelector';
import { AppointmentSlotSelector } from '~/components/appointments/AppointmentSlotSelector';
import { AppointmentHistoryModal } from '~/components/appointments/AppointmentHistoryModal';
import {
    countCalledOff,
    countLateCancellations,
    countLateMoves,
    countMovedByKalia,
} from '~/components/appointments/appointmentHistory';
import { useAppointmentsCalendarStore } from '~/stores/appointments-calendar-store';
import { Button } from '~/components/ui/button';
import { trpc } from '~/api/trpc';
import { cn } from '~/lib/utils';
import { History } from 'lucide-react';
import { useState } from 'react';

type AppointmentBuilderProps = {
    calendarRef?: React.RefObject<FullCalendar | null>;
};

export const AppointmentBuilder = ({ calendarRef }: AppointmentBuilderProps) => {
    const { selectedStore } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            selectedStore: state.selectedStore,
        }))
    )

    const { steps, mode, appointmentId } = useCreateOrModifyAppointmentStore(
        useShallow(state => ({
            steps: state.steps,
            mode: state.mode,
            appointmentId: state.appointmentId,
        }))
    );

    const [isSlotFinderOpen, setIsSlotFinderOpen] = useState(false);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);

    const showHistory = mode === 'modify' && !!appointmentId;
    const historyQuery = trpc.appointments.getAppointmentModificationHistory.useQuery(
        { appointmentId: appointmentId ?? '' },
        { enabled: showHistory }
    );
    const historyEntries = historyQuery.data ?? [];
    const kaliaCount = countCalledOff(historyEntries) + countMovedByKalia(historyEntries);
    const customerLateCount = countLateMoves(historyEntries) + countLateCancellations(historyEntries);

    return (
        <div className='space-y-6'>
            <div className='p-4 rounded-xl border border-gray-200 bg-gray-50 space-y-2'>
                <BookableItemSearch storeId={selectedStore?.id || undefined} />
                <StepList />
            </div>

            {steps.length > 0 && (
                <div className='space-y-4'>
                    <div className='flex items-center gap-2'>
                        <AppointmentSlotSelector 
                            storeId={selectedStore?.id} 
                            isOpen={isSlotFinderOpen}
                            onOpenChange={setIsSlotFinderOpen}
                        />
                        {showHistory && appointmentId && (
                            <div className='flex flex-col items-start gap-1'>
                                <Button variant='outline' size='lg' onClick={() => setIsHistoryOpen(true)}>
                                    <History className='mr-2 h-5 w-5' />
                                    Stornierungen und Verschiebungen
                                </Button>
                                {!historyQuery.isLoading && (
                                    <p className='text-xs font-semibold'>
                                        <span className={cn(kaliaCount > 0 ? 'text-yellow-500' : 'text-black')}>
                                            von Kalia: {kaliaCount}
                                        </span>
                                        {' · '}
                                        <span className={cn(customerLateCount > 0 ? 'text-red-500' : 'text-black')}>
                                            vom Kunden: {customerLateCount}
                                        </span>
                                    </p>
                                )}
                                <AppointmentHistoryModal
                                    appointmentId={appointmentId}
                                    open={isHistoryOpen}
                                    onClose={() => setIsHistoryOpen(false)}
                                />
                            </div>
                        )}
                    </div>
                    <DateTimeSelector onOpenSlotFinder={() => setIsSlotFinderOpen(true)} />
                </div>
            )}
        </div>
    );
};